import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toast } from "../../components/ui-components/Toast";
import Modal from "../../components/ui-components/Modal";
import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot";
import { primaryButtonCls, secondaryButtonCls, destructiveButtonCls } from "../../components/ui-components/buttonStyles";
import Toggle from "../../components/ui-components/Toggle";
import Input from "../../components/form-components/Input";
import FormButton from "../../components/form-components/FormButton";
import Badge from "../../components/ui-components/Badge";
import Skeleton from "../../components/ui-components/Skeleton";
import { Dropdown } from "../../components/form-components/Dropdown";
import {
  getSchedule,
  updateSchedule,
  deleteSchedule,
  triggerSchedule,
  pauseSchedule,
  resumeSchedule,
  getScheduleHistory,
  getPipeline,
} from "./api";
import PipelineStageStrip from "./PipelineStageStrip";
import { cronToHuman, formatDuration, formatRelativeTime } from "./utils";

const FREQUENCY_ITEMS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DAY_OPTIONS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "America/New_York", label: "America/New_York" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai" },
];

function parseCron(expression) {
  const defaults = { frequency: "daily", hour: "02", minute: "00", dayOfWeek: "1", dayOfMonth: "1" };
  if (!expression) return { ...defaults, isAdvanced: false };
  const stripped = expression.replace(/^CRON_TZ=\S+\s+/, "");
  const parts = stripped.split(/\s+/);
  if (parts.length < 5) return { ...defaults, isAdvanced: true };
  const [minute, hour, dom, month, dow] = parts;
  if (month !== "*") return { ...defaults, hour: hour || "02", minute: minute || "00", isAdvanced: true };
  if (dom !== "*" && dow !== "*") return { ...defaults, hour: hour || "02", minute: minute || "00", isAdvanced: true };
  const parsedDom = parseInt(dom, 10);
  if (dom !== "*" && (isNaN(parsedDom) || parsedDom < 1 || parsedDom > 31)) return { ...defaults, hour: hour || "02", minute: minute || "00", isAdvanced: true };

  let parsed;
  if (dom !== "*") parsed = { ...defaults, frequency: "monthly", hour: hour || "02", minute: minute || "00", dayOfMonth: dom };
  else if (dow !== "*" && /^\d+$/.test(dow)) parsed = { ...defaults, frequency: "weekly", hour: hour || "02", minute: minute || "00", dayOfWeek: dow };
  else if (dow !== "*") return { ...defaults, hour: hour || "02", minute: minute || "00", isAdvanced: true };
  else parsed = { ...defaults, frequency: "daily", hour: hour || "02", minute: minute || "00" };

  const canonical = buildCron(parsed);
  return { ...parsed, isAdvanced: canonical !== stripped };
}

function buildCron({ frequency, hour, minute, dayOfWeek, dayOfMonth }) {
  const m = minute || "0";
  const h = hour || "0";
  if (frequency === "weekly") return `${m} ${h} * * ${dayOfWeek}`;
  if (frequency === "monthly") return `${m} ${h} ${dayOfMonth} * *`;
  return `${m} ${h} * * *`;
}

function resolveDetailStatus(schedule) {
  if (schedule.last_run_status === "running") return { badge: "running", label: "Running" };
  if (schedule.current_status === "paused") return { badge: "stopped", label: "Paused" };
  if (schedule.current_status === "active") return { badge: "success", label: "Active" };
  return { badge: schedule.current_status || "pending", label: schedule.current_status || "Idle" };
}

const StatusCard = ({ label, children, subtext }) => (
  <div className="bg-card rounded-card border border-card-bd p-4">
    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">{label}</div>
    {children}
    {subtext && <div className="text-xs text-text-2 mt-1">{subtext}</div>}
  </div>
);

const DataImportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rawCron, setRawCron] = useState("");
  const [expandedRunId, setExpandedRunId] = useState(null);

  const [frequency, setFrequency] = useState("daily");
  const [hour, setHour] = useState("02");
  const [minute, setMinute] = useState("00");
  const [dayOfWeek, setDayOfWeek] = useState("1");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [timezone, setTimezone] = useState({ value: "UTC", label: "UTC" });
  const [timeout, setTimeout_] = useState("3600");

  const { data: scheduleData, isLoading, isError: scheduleError, error: scheduleFetchError } = useQuery({
    queryKey: ["schedule", id],
    queryFn: () => getSchedule(id),
    refetchInterval: 10000,
  });

  const { data: historyData, isError: historyError } = useQuery({
    queryKey: ["schedule-history", id],
    queryFn: () => getScheduleHistory(id, 20),
    refetchInterval: 10000,
  });

  // Stage list for the strip. `schedule` is not assigned until the next line, so read
  // the pipeline id off the raw query payload here.
  const pipelineId = scheduleData?.schedule?.pipeline_id;
  const { data: pipelineData } = useQuery({
    queryKey: ["pipeline", pipelineId],
    queryFn: () => getPipeline(pipelineId),
    enabled: !!pipelineId,
  });

  const schedule = scheduleData?.schedule;
  const history = historyError ? [] : (historyData?.history || []);

  useSetHeaderRecordName(schedule?.job_name || null);

  useEffect(() => {
    if (!schedule) return;
    const parsed = parseCron(schedule.schedule_expression);
    setFrequency(parsed.frequency);
    setHour(parsed.hour);
    setMinute(parsed.minute);
    setDayOfWeek(parsed.dayOfWeek);
    setDayOfMonth(parsed.dayOfMonth);
    setTimezone({ value: schedule.timezone || "UTC", label: schedule.timezone || "UTC" });
    setTimeout_(String(schedule.timeout_seconds ?? 3600));
    setRawCron(schedule.schedule_expression || "");
    setShowAdvanced(parsed.isAdvanced);
  }, [schedule]);

  const currentCron = showAdvanced ? rawCron : buildCron({ frequency, hour, minute, dayOfWeek, dayOfMonth });
  const isDirty = schedule && (
    currentCron !== schedule.schedule_expression ||
    timezone.value !== (schedule.timezone || "UTC") ||
    timeout !== String(schedule.timeout_seconds ?? 3600)
  );

  const updateMutation = useMutation({
    mutationFn: (body) => updateSchedule(id, body),
    onSuccess: () => {
      toast.success("Schedule updated");
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to update schedule"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSchedule(id),
    onSuccess: () => {
      toast.success("Job deleted");
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      navigate(schedule?.job_type === "report_generation" ? "/reporting-jobs" : "/data-pipeline");
    },
    onError: (err) => toast.error(err?.message || "Failed to delete job"),
  });

  const triggerMutation = useMutation({
    mutationFn: () => triggerSchedule(id),
    onSuccess: () => {
      toast.success("Job triggered");
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      queryClient.invalidateQueries({ queryKey: ["schedule-history", id] });
    },
    onError: (err) => toast.error(err?.message || "Failed to trigger job"),
  });

  const pauseResumeMutation = useMutation({
    mutationFn: () =>
      schedule?.current_status === "paused" ? resumeSchedule(id) : pauseSchedule(id),
    onSuccess: () => {
      toast.success(schedule?.current_status === "paused" ? "Job resumed" : "Job paused");
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
    onError: (err) => toast.error(err?.message || "Action failed"),
  });

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      schedule_expression: currentCron,
      timezone: timezone.value,
      timeout_seconds: Number.isFinite(parseInt(timeout, 10)) ? parseInt(timeout, 10) : 3600,
    });
  }, [currentCron, timezone, timeout, updateMutation]);

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={64} className="mb-8" />
        <Skeleton height={200} />
      </div>
    );
  }

  if (scheduleError) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-text mb-2">Error loading schedule</h2>
        <p className="text-sm text-text-2 mb-4">{scheduleFetchError?.message || "An unexpected error occurred"}</p>
        <FormButton title="Back" onClick={() => navigate(-1)} />
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-text mb-2">Schedule not found</h2>
        <FormButton title="Back" onClick={() => navigate(-1)} />
      </div>
    );
  }

  const sourceId = schedule.metadata?.source_id || schedule.job_id || "";
  const credType = schedule.metadata?.credential_type || "";
  const isPaused = schedule.current_status === "paused";

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {schedule.job_name || sourceId}
          </div>
          <Badge status={resolveDetailStatus(schedule).badge} label={resolveDetailStatus(schedule).label} />
        </div>
      </HeaderPortal>

      {/* Action buttons live in the global top bar (next to the tab switcher), matching
          /approvals/:id — primary rightmost, shared button styles. */}
      <HeaderPortal>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={secondaryButtonCls}
            onClick={() => { if (!pauseResumeMutation.isLoading) pauseResumeMutation.mutate(); }}
            disabled={pauseResumeMutation.isLoading}
          >
            {isPaused
              ? <FontAwesomeIcon icon={icon({ name: "play", family: "sharp", style: "solid" })} className="mr-2" />
              : <FontAwesomeIcon icon={icon({ name: "pause", family: "sharp", style: "solid" })} className="mr-2" />}
            {isPaused ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            className={destructiveButtonCls}
            onClick={() => setShowDeleteModal(true)}
            disabled={deleteMutation.isLoading}
          >
            <FontAwesomeIcon icon={icon({ name: "trash", style: "solid" })} className="mr-2" />
            Delete
          </button>
          <button
            type="button"
            className={primaryButtonCls}
            onClick={() => { if (!triggerMutation.isLoading) triggerMutation.mutate(); }}
            disabled={triggerMutation.isLoading || schedule.last_run_status === "running"}
          >
            <FontAwesomeIcon icon={icon({ name: "play", family: "sharp", style: "solid" })} className="mr-2" />
            Run Now
          </button>
        </div>
      </HeaderPortal>

      <div className="flex-1 overflow-auto p-6">
        {/* Info line */}
        <div className="text-sm text-text-2 mb-6">
          {sourceId}{credType ? ` · ${credType.replace("_", " ")}` : ""} · {cronToHuman(schedule.schedule_expression)} · {timezone.value}
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatusCard label="Last Run" subtext={schedule.last_run_status && schedule.last_run_status !== "running" ? `Status: ${schedule.last_run_status}` : null}>
            {schedule.last_run_status === "running" ? (
              <div className="flex items-center gap-2">
                <Badge status="running" />
                {schedule.last_run_timestamp && (
                  <span className="text-sm text-text-2">Started {formatRelativeTime(schedule.last_run_timestamp)}</span>
                )}
              </div>
            ) : (
              <div className="text-base font-semibold text-text">{formatRelativeTime(schedule.last_run_timestamp)}</div>
            )}
          </StatusCard>
          <StatusCard label="Next Run" subtext={!isPaused && schedule.next_run_timestamp ? new Date(schedule.next_run_timestamp).toLocaleString() : null}>
            <div className="text-base font-semibold text-text">{isPaused ? "Paused" : formatRelativeTime(schedule.next_run_timestamp)}</div>
          </StatusCard>
          <StatusCard label="Duration" subtext={schedule.last_execution_time_ms ? "Last run" : null}>
            <div className="text-base font-semibold text-text">{formatDuration(schedule.last_execution_time_ms)}</div>
          </StatusCard>
          <StatusCard label="Priority">
            <div className="text-base font-semibold text-text">{schedule.priority || 5}</div>
          </StatusCard>
        </div>

        {/* Two Column Layout */}
        <div className="flex gap-6 mb-6">
          {/* Left column — Schedule Config */}
          <div className="flex-1 bg-card rounded-card border border-card-bd overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <span className="text-base font-semibold text-text">Schedule Configuration</span>
            </div>
            <div className="p-5">
              {!showAdvanced && (
                <>
                  <div className="mb-4">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Frequency</div>
                    <Toggle items={FREQUENCY_ITEMS} value={frequency} changeHandler={setFrequency} style="buttons" />
                  </div>

                  {frequency === "weekly" && (
                    <div className="mb-4">
                      <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Day of Week</div>
                      <Dropdown
                        options={DAY_OPTIONS}
                        selectedValue={DAY_OPTIONS.find((d) => d.value === dayOfWeek)}
                        setSelectedValue={(v) => setDayOfWeek(v.value)}
                        dropdownWidth={200}
                      />
                    </div>
                  )}

                  {frequency === "monthly" && (
                    <div className="mb-4">
                      <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Day of Month</div>
                      <Input type="number" name="day-of-month" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} width={80} noMarginBottom />
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Run Time</div>
                    <div className="flex items-center gap-2">
                      <Input type="text" name="hour" value={`${hour}:${minute}`} onChange={(e) => {
                        const parts = e.target.value.split(":");
                        if (parts.length !== 2) return;
                        const [h, m] = parts;
                        const ph = parseInt(h, 10);
                        const pm = parseInt(m, 10);
                        if (!isNaN(ph) && ph >= 0 && ph <= 23) setHour(String(ph).padStart(2, "0"));
                        if (!isNaN(pm) && pm >= 0 && pm <= 59) setMinute(String(pm).padStart(2, "0"));
                      }} width={80} noMarginBottom />
                      <span className="text-sm text-text-2">{timezone.value}</span>
                    </div>
                  </div>
                </>
              )}

              <div className="mb-4 pt-2 border-t border-border">
                <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs text-text-2 hover:text-text cursor-pointer">
                  {showAdvanced ? "▾ Hide Advanced" : "▸ Advanced (CRON expression)"}
                </button>
                {showAdvanced && (
                  <div className="mt-2">
                    <Input type="text" name="cron" value={rawCron} onChange={(e) => setRawCron(e.target.value)} placeholder="0 2 * * *" width={300} noMarginBottom />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Timezone</div>
                <Dropdown options={TIMEZONE_OPTIONS} selectedValue={timezone} setSelectedValue={setTimezone} dropdownWidth={220} />
              </div>

              <div className="mb-4">
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Timeout (seconds)</div>
                <Input type="number" name="timeout" value={timeout} onChange={(e) => setTimeout_(e.target.value)} width={100} noMarginBottom />
              </div>

              <div className="mt-5">
                <FormButton title="Save Changes" style="primary" onClick={handleSave} disabled={!isDirty || updateMutation.isLoading} />
              </div>
            </div>
          </div>

          {/* Right column — Summary & Pipeline */}
          <div className="flex-1 flex flex-col gap-4">
            {schedule.pipeline_id && (
              <div className="bg-card rounded-card border border-card-bd overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <span className="text-base font-semibold text-text">Pipeline</span>
                </div>
                <div className="p-5">
                  <PipelineStageStrip
                    stages={pipelineData?.stages}
                    activeStage={schedule.pipeline_stage}
                  />
                </div>
              </div>
            )}

            {/* Job Summary */}
            {(() => {
              const completed = history.filter((r) => r.status === "success" || r.status === "failed");
              const succeeded = history.filter((r) => r.status === "success");
              const failed = history.filter((r) => r.status === "failed");
              const durations = history.filter((r) => r.execution_time_ms > 0).map((r) => r.execution_time_ms);
              const avgDuration = durations.length > 0 ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
              const lastFailure = failed.length > 0 ? failed[0] : null;
              const successRate = completed.length > 0 ? Math.round((succeeded.length / completed.length) * 100) : null;

              return (
                <div className="bg-card rounded-card border border-card-bd overflow-hidden">
                  <div className="px-5 py-3 border-b border-border">
                    <span className="text-base font-semibold text-text">Job Summary</span>
                  </div>
                  <div className="p-5">
                    {completed.length === 0 ? (
                      <p className="text-sm text-text-2">No completed runs yet.</p>
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div>
                          <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1.5">Success Rate</div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-surface-2 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${successRate === 100 ? "bg-success" : successRate >= 80 ? "bg-success" : successRate >= 50 ? "bg-warning" : "bg-danger"}`}
                                style={{ width: `${successRate}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-text tabular-nums">{succeeded.length}/{completed.length}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Avg Duration</div>
                          <div className="text-sm font-semibold text-text">{avgDuration ? formatDuration(avgDuration) : "—"}</div>
                        </div>
                        {lastFailure && (
                          <div>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Last Failure</div>
                            <div className="text-sm text-danger">{formatRelativeTime(lastFailure.started_at)}</div>
                            {lastFailure.error_message && (
                              <div className="text-xs text-text-2 mt-0.5 truncate max-w-full">{lastFailure.error_message}</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Run History Table */}
        <div className="bg-card rounded-card border border-card-bd overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-border">
            <span className="text-base font-semibold text-text">Run History</span>
          </div>
          <div className="p-0">
            {history.length === 0 ? (
              <p className="text-sm text-text-2 p-5">No run history available.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-text-2 tracking-wider border-b border-border">
                    <th className="py-2.5 px-5 font-medium">Started</th>
                    <th className="py-2.5 px-5 font-medium">Completed</th>
                    <th className="py-2.5 px-5 font-medium">Status</th>
                    <th className="py-2.5 px-5 font-medium">Duration</th>
                    <th className="py-2.5 px-5 font-medium">Error</th>
                    <th className="py-2.5 px-5 font-medium w-[60px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => {
                    const isError = run.status && run.status !== "success";
                    const hasError = !!run.error_message;
                    const isExpanded = expandedRunId === run.id;
                    return (
                      <Fragment key={run.id}>
                        <tr
                          className={`border-b border-border ${isExpanded ? "border-b-0" : "last:border-b-0"} ${isError ? "bg-danger-soft" : ""} ${hasError ? "cursor-pointer hover:bg-surface-2/50" : ""}`}
                          onClick={() => hasError && setExpandedRunId(isExpanded ? null : run.id)}
                        >
                          <td className="py-2.5 px-5 text-text">{run.started_at ? new Date(run.started_at).toLocaleString() : "—"}</td>
                          <td className="py-2.5 px-5 text-text">{run.completed_at ? new Date(run.completed_at).toLocaleString() : "—"}</td>
                          <td className="py-2.5 px-5"><Badge status={run.status} /></td>
                          <td className="py-2.5 px-5 text-text-2">{formatDuration(run.execution_time_ms)}</td>
                          <td className="py-2.5 px-5 text-danger text-xs">
                            {hasError ? (
                              <div className="flex items-center gap-1.5">
                                <span className={isExpanded ? "" : "max-w-[300px] truncate block"}>{run.error_message}</span>
                                <FontAwesomeIcon
                                  icon={isExpanded
                                    ? icon({ name: "chevron-up", family: "sharp", style: "solid" })
                                    : icon({ name: "chevron-down", family: "sharp", style: "solid" })
                                  }
                                  className="text-text-2 text-[10px] flex-shrink-0"
                                />
                              </div>
                            ) : "—"}
                          </td>
                          <td className="py-2.5 px-5">
                            {run.metadata?.log_id ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/data-pipeline/log/${run.metadata.log_id}`); }}
                                className="text-primary hover:text-primary-hover cursor-pointer text-xs whitespace-nowrap"
                                title="View activity log"
                              >
                                <FontAwesomeIcon icon={icon({ name: "arrow-up-right-from-square", family: "sharp", style: "solid" })} className="mr-1" />
                                Log
                              </button>
                            ) : null}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className={`border-b border-border last:border-b-0 ${isError ? "bg-danger-soft" : ""}`}>
                            <td colSpan={6} className="px-5 pb-4 pt-0">
                              <div className="bg-danger-soft border border-danger/30 rounded-lg p-3 text-xs text-danger font-mono whitespace-pre-wrap break-all">
                                {run.error_message}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        title={`Delete ${schedule.job_type === "report_generation" ? "Reporting" : "Import"} Job`}
        description={`Are you sure you want to delete this ${schedule.job_type === "report_generation" ? "reporting" : "import"} job? This action cannot be undone.`}
        confirmButton="Delete"
        confirmAction={() => deleteMutation.mutate()}
        size="sm"
      />
    </div>
  );
};

export default DataImportDetailPage;
